(function () {
  if (window.MDStickerEngine) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const imageWidth = (image) => image ? (image.naturalWidth || image.width || 1) : 1;
  const imageHeight = (image) => image ? (image.naturalHeight || image.height || 1) : 1;

  function normaliseHex(value) {
    const raw = String(value || '').trim().replace(/^#/, '');
    if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw}`.toLowerCase();
    return '';
  }

  function hexToRgb(value) {
    const hex = normaliseHex(value);
    if (!hex) return null;
    return {
      r: parseInt(hex.slice(1, 3), 16),
      g: parseInt(hex.slice(3, 5), 16),
      b: parseInt(hex.slice(5, 7), 16)
    };
  }

  function transformedArt(image, width, height, reflection) {
    const art = document.createElement('canvas');
    art.width = Math.max(1, Math.round(width));
    art.height = Math.max(1, Math.round(height));
    const ctx = art.getContext('2d');
    ctx.imageSmoothingEnabled = art.width !== imageWidth(image) || art.height !== imageHeight(image);
    ctx.imageSmoothingQuality = 'high';
    if (reflection === 'mirror-x') {
      ctx.translate(art.width, 0);
      ctx.scale(-1, 1);
    } else if (reflection === 'mirror-y') {
      ctx.translate(0, art.height);
      ctx.scale(1, -1);
    } else if (reflection === 'rotate-180') {
      ctx.translate(art.width, art.height);
      ctx.rotate(Math.PI);
    } else if (reflection === 'rotate-180-mirror-x') {
      ctx.translate(0, art.height);
      ctx.scale(1, -1);
    }
    ctx.drawImage(image, 0, 0, art.width, art.height);
    return art;
  }

  function removeWhiteFromArt(art, enabled = true) {
    if (!enabled) return art;
    const cleaned = document.createElement('canvas');
    cleaned.width = art.width;
    cleaned.height = art.height;
    const ctx = cleaned.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(art, 0, 0);
    const pixels = ctx.getImageData(0, 0, cleaned.width, cleaned.height);
    const data = pixels.data;
    const background = whiteBackgroundMask(data, cleaned.width, cleaned.height);
    for (let p = 0; p < background.length; p++) {
      if (background[p]) data[p * 4 + 3] = 0;
    }
    removeWhiteEdgeArtifacts(data, background, cleaned.width, cleaned.height);
    ctx.putImageData(pixels, 0, 0);
    return cleaned;
  }

  function whiteBackgroundMask(pixels, width, height) {
    const remove = new Uint8Array(width * height);
    const queued = new Uint8Array(width * height);
    const queue = [];
    const isWhite = (index) => {
      const i = index * 4;
      return pixels[i + 3] > 18 && pixels[i] > 232 && pixels[i + 1] > 232 && pixels[i + 2] > 232;
    };
    const add = (index) => {
      if (index < 0 || index >= queued.length || queued[index] || !isWhite(index)) return;
      queued[index] = 1;
      queue.push(index);
    };
    for (let x = 0; x < width; x++) {
      add(x);
      add((height - 1) * width + x);
    }
    for (let y = 0; y < height; y++) {
      add(y * width);
      add(y * width + width - 1);
    }
    for (let q = 0; q < queue.length; q++) {
      const index = queue[q];
      remove[index] = 1;
      const x = index % width;
      if (x > 0) add(index - 1);
      if (x < width - 1) add(index + 1);
      if (index >= width) add(index - width);
      if (index < width * (height - 1)) add(index + width);
    }
    return remove;
  }

  function removeWhiteEdgeArtifacts(pixels, background, width, height) {
    const nearRemoved = (index, radius) => {
      const x = index % width;
      const y = Math.floor(index / width);
      for (let dy = -radius; dy <= radius; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= height) continue;
        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= width || (dx === 0 && dy === 0)) continue;
          if (background[ny * width + nx]) return true;
        }
      }
      return false;
    };
    for (let p = 0; p < background.length; p++) {
      if (background[p] || !nearRemoved(p, 2)) continue;
      const i = p * 4;
      const max = Math.max(pixels[i], pixels[i + 1], pixels[i + 2]);
      const min = Math.min(pixels[i], pixels[i + 1], pixels[i + 2]);
      const lowSaturation = max - min < 42;
      if (max > 190 && lowSaturation) {
        const fade = max > 232 ? 0 : clamp((232 - max) / 42, 0, 1);
        pixels[i + 3] = Math.round(pixels[i + 3] * fade);
        if (pixels[i + 3] < 18) pixels[i + 3] = 0;
      }
    }
  }

  function cleanTransparentEdges(canvas, alphaCutoff = 48) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 3; i < pixels.data.length; i += 4) {
      if (pixels.data[i] > 0 && pixels.data[i] < alphaCutoff) pixels.data[i] = 0;
    }
    ctx.putImageData(pixels, 0, 0);
    return canvas;
  }

  function matteSemiTransparentEdges(canvas, borderColour, borderPx) {
    if (!borderPx) return canvas;
    const border = hexToRgb(borderColour);
    if (!border) return canvas;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = pixels.data;
    const transparent = new Uint8Array(canvas.width * canvas.height);
    for (let p = 0; p < transparent.length; p++) transparent[p] = data[p * 4 + 3] < 96 ? 1 : 0;
    const touchesTransparent = (index) => {
      const x = index % canvas.width;
      const y = Math.floor(index / canvas.width);
      for (let dy = -2; dy <= 2; dy++) {
        const ny = y + dy;
        if (ny < 0 || ny >= canvas.height) continue;
        for (let dx = -2; dx <= 2; dx++) {
          const nx = x + dx;
          if (nx < 0 || nx >= canvas.width || (dx === 0 && dy === 0)) continue;
          if (transparent[ny * canvas.width + nx]) return true;
        }
      }
      return false;
    };
    for (let p = 0; p < transparent.length; p++) {
      const i = p * 4;
      const alpha = data[i + 3];
      if (alpha <= 16 || alpha >= 252 || !touchesTransparent(p)) continue;
      data[i] = border.r;
      data[i + 1] = border.g;
      data[i + 2] = border.b;
    }
    ctx.putImageData(pixels, 0, 0);
    return canvas;
  }

  function imageToMask(canvas, alphaCutoff = 48) {
    const data = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, canvas.width, canvas.height).data;
    const mask = new Uint8Array(canvas.width * canvas.height);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) mask[p] = data[i + 3] >= alphaCutoff ? 1 : 0;
    return { width: canvas.width, height: canvas.height, data: mask };
  }

  function findMaskBox(mask) {
    let minX = mask.width;
    let minY = mask.height;
    let maxX = 0;
    let maxY = 0;
    let found = false;
    for (let y = 0; y < mask.height; y++) {
      for (let x = 0; x < mask.width; x++) {
        if (!mask.data[y * mask.width + x]) continue;
        found = true;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
    return found ? { minX, minY, maxX, maxY } : { minX: 0, minY: 0, maxX: mask.width, maxY: mask.height };
  }

  function padMask(mask, pad) {
    if (pad <= 0) return { mask, pad: 0 };
    const width = mask.width + pad * 2;
    const height = mask.height + pad * 2;
    const data = new Uint8Array(width * height);
    for (let y = 0; y < mask.height; y++) {
      data.set(mask.data.subarray(y * mask.width, y * mask.width + mask.width), (y + pad) * width + pad);
    }
    return { mask: { width, height, data }, pad };
  }

  function dilate(mask, radius) {
    const out = new Uint8Array(mask.width * mask.height);
    const dist = new Float32Array(mask.width * mask.height);
    const inf = mask.width + mask.height + radius;
    for (let i = 0; i < dist.length; i++) dist[i] = mask.data[i] ? 0 : inf;
    for (let y = 0; y < mask.height; y++) {
      for (let x = 0; x < mask.width; x++) {
        const i = y * mask.width + x;
        let best = dist[i];
        if (x > 0) best = Math.min(best, dist[i - 1] + 1);
        if (y > 0) best = Math.min(best, dist[i - mask.width] + 1);
        if (x > 0 && y > 0) best = Math.min(best, dist[i - mask.width - 1] + 1.414);
        if (x < mask.width - 1 && y > 0) best = Math.min(best, dist[i - mask.width + 1] + 1.414);
        dist[i] = best;
      }
    }
    for (let y = mask.height - 1; y >= 0; y--) {
      for (let x = mask.width - 1; x >= 0; x--) {
        const i = y * mask.width + x;
        let best = dist[i];
        if (x < mask.width - 1) best = Math.min(best, dist[i + 1] + 1);
        if (y < mask.height - 1) best = Math.min(best, dist[i + mask.width] + 1);
        if (x < mask.width - 1 && y < mask.height - 1) best = Math.min(best, dist[i + mask.width + 1] + 1.414);
        if (x > 0 && y < mask.height - 1) best = Math.min(best, dist[i + mask.width - 1] + 1.414);
        dist[i] = best;
      }
    }
    for (let i = 0; i < dist.length; i++) out[i] = dist[i] <= radius ? 1 : 0;
    return { width: mask.width, height: mask.height, data: out };
  }

  function exteriorMask(mask) {
    const outside = new Uint8Array(mask.width * mask.height);
    const queue = [];
    const add = (x, y) => {
      if (x < 0 || y < 0 || x >= mask.width || y >= mask.height) return;
      const index = y * mask.width + x;
      if (outside[index] || mask.data[index]) return;
      outside[index] = 1;
      queue.push(index);
    };
    for (let x = 0; x < mask.width; x++) {
      add(x, 0);
      add(x, mask.height - 1);
    }
    for (let y = 0; y < mask.height; y++) {
      add(0, y);
      add(mask.width - 1, y);
    }
    for (let i = 0; i < queue.length; i++) {
      const index = queue[i];
      const x = index % mask.width;
      const y = Math.floor(index / mask.width);
      add(x - 1, y);
      add(x + 1, y);
      add(x, y - 1);
      add(x, y + 1);
    }
    return outside;
  }

  function removeDuplicatePoints(points) {
    const out = [];
    points.forEach((point) => {
      const last = out[out.length - 1];
      if (!last || last.x !== point.x || last.y !== point.y) out.push(point);
    });
    if (out.length > 1) {
      const first = out[0];
      const last = out[out.length - 1];
      if (first.x === last.x && first.y === last.y) out.pop();
    }
    return out;
  }

  function polygonArea(points) {
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const current = points[i];
      const next = points[(i + 1) % points.length];
      area += current.x * next.y - next.x * current.y;
    }
    return area / 2;
  }

  function traceBoundaryLoops(mask, outside) {
    const edgeMap = new Map();
    const addEdge = (fromX, fromY, toX, toY) => {
      const key = `${fromX},${fromY}`;
      if (!edgeMap.has(key)) edgeMap.set(key, []);
      edgeMap.get(key).push({ fromX, fromY, toX, toY });
    };
    for (let y = 0; y < mask.height; y++) {
      for (let x = 0; x < mask.width; x++) {
        const index = y * mask.width + x;
        if (!mask.data[index]) continue;
        if (y === 0 || outside[index - mask.width]) addEdge(x, y, x + 1, y);
        if (x === mask.width - 1 || outside[index + 1]) addEdge(x + 1, y, x + 1, y + 1);
        if (y === mask.height - 1 || outside[index + mask.width]) addEdge(x + 1, y + 1, x, y + 1);
        if (x === 0 || outside[index - 1]) addEdge(x, y + 1, x, y);
      }
    }
    const loops = [];
    while (edgeMap.size) {
      const firstKey = edgeMap.keys().next().value;
      const firstBucket = edgeMap.get(firstKey);
      const first = firstBucket.pop();
      if (!firstBucket.length) edgeMap.delete(firstKey);
      const loop = [{ x: first.fromX, y: first.fromY }];
      let current = first;
      let guard = 0;
      while (guard++ < mask.width * mask.height * 4) {
        loop.push({ x: current.toX, y: current.toY });
        const nextKey = `${current.toX},${current.toY}`;
        const bucket = edgeMap.get(nextKey);
        if (!bucket || !bucket.length) break;
        current = bucket.pop();
        if (!bucket.length) edgeMap.delete(nextKey);
        if (current.toX === first.fromX && current.toY === first.fromY) {
          loop.push({ x: current.toX, y: current.toY });
          break;
        }
      }
      if (loop.length > 12) loops.push(removeDuplicatePoints(loop));
    }
    return loops;
  }

  function limitPathPoints(points, maxPoints) {
    if (points.length <= maxPoints) return points;
    const stride = points.length / maxPoints;
    const out = [];
    for (let i = 0; i < maxPoints; i++) out.push(points[Math.floor(i * stride)]);
    return out;
  }

  function resamplePath(points, minDistance) {
    if (minDistance <= 1) return points;
    const out = [];
    let last = null;
    points.forEach((point) => {
      if (!last || Math.hypot(point.x - last.x, point.y - last.y) >= minDistance) {
        out.push(point);
        last = point;
      }
    });
    return out.length > 8 ? out : points;
  }

  function roundedRectPath(x, y, width, height, radius) {
    const points = [];
    const r = Math.max(0, Math.min(radius, Math.min(width, height) / 2));
    const corners = [
      { cx: x + width - r, cy: y + r, start: -Math.PI / 2 },
      { cx: x + width - r, cy: y + height - r, start: 0 },
      { cx: x + r, cy: y + height - r, start: Math.PI / 2 },
      { cx: x + r, cy: y + r, start: Math.PI }
    ];
    corners.forEach((corner) => {
      for (let i = 0; i <= 12; i++) {
        const angle = corner.start + (i / 12) * (Math.PI / 2);
        points.push({ x: corner.cx + Math.cos(angle) * r, y: corner.cy + Math.sin(angle) * r });
      }
    });
    return points;
  }

  function traceOuterContour(mask) {
    const loops = traceBoundaryLoops(mask, exteriorMask(mask));
    if (!loops.length) {
      const box = findMaskBox(mask);
      return roundedRectPath(box.minX, box.minY, box.maxX - box.minX, box.maxY - box.minY, 18);
    }
    const points = loops.sort((a, b) => Math.abs(polygonArea(b)) - Math.abs(polygonArea(a)))[0];
    return limitPathPoints(resamplePath(points, Math.max(1, Math.round(Math.max(mask.width, mask.height) / 1000))), 1400);
  }

  function averagePath(points, radius) {
    return points.map((point, index) => {
      let x = 0;
      let y = 0;
      let total = 0;
      for (let offset = -radius; offset <= radius; offset++) {
        const weight = radius + 1 - Math.abs(offset);
        const sample = points[(index + offset + points.length) % points.length];
        x += sample.x * weight;
        y += sample.y * weight;
        total += weight;
      }
      return { x: x / total, y: y / total };
    });
  }

  function smoothPath(points, amount) {
    let out = points.slice();
    const passes = Math.max(1, Math.round(amount * 0.6));
    const radius = Math.max(1, Math.round(amount * 0.75));
    for (let pass = 0; pass < passes; pass++) out = averagePath(out, radius);
    return limitPathPoints(out, 1400);
  }

  function simplifyPath(points, tolerance) {
    if (points.length < 4) return points;
    const out = [];
    const stride = Math.max(1, Math.round(tolerance / 3));
    for (let i = 0; i < points.length; i += stride) out.push(points[i]);
    return out;
  }

  function drawPath(target, points) {
    target.beginPath();
    if (!points.length) return;
    target.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      const current = points[i];
      const next = points[(i + 1) % points.length];
      target.quadraticCurveTo(current.x, current.y, (current.x + next.x) / 2, (current.y + next.y) / 2);
    }
    target.closePath();
  }

  function makeBorderPath(art, borderPx, options = {}) {
    if (borderPx <= 0) return [];
    const printQuality = !!options.printQuality;
    const smoothingAmount = options.smoothingPx == null ? null : options.smoothingPx;
    const maxMaskEdge = printQuality ? 2200 : 1100;
    const maskScale = Math.min(1, maxMaskEdge / Math.max(art.width, art.height));
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = Math.max(1, Math.round(art.width * maskScale));
    maskCanvas.height = Math.max(1, Math.round(art.height * maskScale));
    const maskCtx = maskCanvas.getContext('2d');
    maskCtx.imageSmoothingEnabled = true;
    maskCtx.imageSmoothingQuality = 'high';
    maskCtx.drawImage(art, 0, 0, maskCanvas.width, maskCanvas.height);
    const radius = Math.max(1, Math.round(borderPx * maskScale));
    const smoothingSource = smoothingAmount === null ? 1 : Math.max(1, Number(smoothingAmount) || 1);
    const smoothing = Math.max(3, smoothingSource * 1.75);
    const padded = padMask(imageToMask(maskCanvas, options.alphaCutoff || 48), Math.ceil(radius + smoothing * 2 + 8));
    const expanded = dilate(padded.mask, radius);
    const contour = traceOuterContour(expanded);
    const smooth = smoothPath(smoothPath(contour, smoothing), Math.max(3, smoothing * 0.45));
    const tolerance = clamp(0.9 + smoothing * 0.22, 0.9, 2.8);
    return simplifyPath(smooth, tolerance).map((point) => ({
      x: (point.x - padded.pad) / maskScale + borderPx,
      y: (point.y - padded.pad) / maskScale + borderPx
    }));
  }

  function makeCutlinePath(panel, layer = {}, options = {}) {
    const cacheKey = [
      Math.round(panel.width),
      Math.round(panel.height),
      Math.round((layer.borderMm || 0) * 100),
      Math.round((layer.smoothingMm || 1) * 100),
      layer.removeWhite ? 1 : 0,
      imageWidth(layer.image),
      imageHeight(layer.image)
    ].join('|');
    if (layer._cutlineCacheKey === cacheKey && layer._cutlinePath) return layer._cutlinePath;
    const maxMaskEdge = options.maxMaskEdge || 760;
    const maskScale = Math.min(1, maxMaskEdge / Math.max(panel.width, panel.height));
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = Math.max(1, Math.round(panel.width * maskScale));
    maskCanvas.height = Math.max(1, Math.round(panel.height * maskScale));
    const maskCtx = maskCanvas.getContext('2d');
    maskCtx.imageSmoothingEnabled = true;
    maskCtx.imageSmoothingQuality = 'high';
    maskCtx.drawImage(panel, 0, 0, maskCanvas.width, maskCanvas.height);
    const contour = traceOuterContour(imageToMask(maskCanvas, options.alphaCutoff || 48));
    const smooth = smoothPath(contour, Math.max(5, Number(layer.smoothingMm || 1) * 1.6));
    const path = simplifyPath(smooth, 1.35).map((point) => ({
      x: point.x / maskScale,
      y: point.y / maskScale
    }));
    layer._cutlineCacheKey = cacheKey;
    layer._cutlinePath = path;
    return path;
  }

  function makePanel(image, width, height, borderPx, options = {}) {
    let art = transformedArt(image, width, height, options.reflection || 'none');
    art = removeWhiteFromArt(art, options.removeWhite);
    if (options.cleanTransparentEdges) cleanTransparentEdges(art, options.edgeAlphaCutoff || 48);
    matteSemiTransparentEdges(art, options.borderColour || '#ffffff', borderPx);
    const contentBox = findMaskBox(imageToMask(art, options.alphaCutoff || 48));
    const panel = document.createElement('canvas');
    panel.width = Math.max(1, art.width + borderPx * 2);
    panel.height = Math.max(1, art.height + borderPx * 2);
    const panelCtx = panel.getContext('2d');
    panelCtx.imageSmoothingEnabled = true;
    panelCtx.imageSmoothingQuality = 'high';
    const borderPath = makeBorderPath(art, borderPx, {
      printQuality: !!options.preserveArtwork,
      smoothingPx: options.smoothingPx,
      alphaCutoff: options.alphaCutoff || 48
    });
    if (borderPath.length) {
      drawPath(panelCtx, borderPath);
      panelCtx.fillStyle = options.borderColour || '#ffffff';
      panelCtx.fill();
    }
    panelCtx.drawImage(art, borderPx, borderPx);
    panel._contentBox = {
      x: borderPx + contentBox.minX,
      y: borderPx + contentBox.minY,
      width: Math.max(1, contentBox.maxX - contentBox.minX),
      height: Math.max(1, contentBox.maxY - contentBox.minY)
    };
    return panel;
  }

  window.MDStickerEngine = {
    imageWidth,
    imageHeight,
    transformedArt,
    removeWhiteFromArt,
    cleanTransparentEdges,
    matteSemiTransparentEdges,
    imageToMask,
    findMaskBox,
    drawPath,
    makeBorderPath,
    makeCutlinePath,
    makePanel
  };
})();